import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Eye, XCircle, Camera, Check, CheckCircle2, ShieldCheck, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet } from '../../lib/api';
import { formatDate } from '../../lib/format';
import Pagination from '../../components/Pagination';
import { useAuth } from '../../contexts/AuthContext';
import { VendorNameInput } from '../../components/pickers/VendorNameInput';
import { POSearchSelect } from '../../components/pickers/POSearchSelect';
import { BOQSearchSelect, ProjectSearchSelect } from '../../components/pickers/BOQSearchSelect';
import { NonPOItemInput } from '../../components/stores/NonPOItemInput';
import { GRNDetailModal, InspectionBadge, GRNStatusBadges } from '../../components/stores/GRNDetailModal';
import { canRunInspection, compressImageFile, EVIDENCE_META } from './stores-constants';

const API = process.env.REACT_APP_BACKEND_URL;
const LOC_SHORT = { project: 'Site', office: 'Office', godown: 'Godown' };
const QC_CHIPS = [['', 'All'], ['pending', 'QC Pending'], ['approved', 'Approved'], ['partial', 'Partial'], ['rejected', 'Rejected'], ['cancelled', 'Cancelled']];
const TYPE_CHIPS = [['', 'PO + Non-PO'], ['po', 'PO'], ['non_po', 'Non-PO']];
const LOC_CHIPS = [['', 'All locations'], ['project', 'Site'], ['office', 'Office'], ['godown', 'Godown']];

// ========== GRN TAB ==========
export function GRNTab({ projectId, projects = [] }) {
  const { user } = useAuth();
  const [grns, setGRNs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [viewGrn, setViewGrn] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [filters, setFilters] = useState({ type: '', qc_status: '', location_type: '', date_from: '', date_to: '' });
  const canInspect = canRunInspection(user);
  const setFilter = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(1); };
  useEffect(() => { const t = setTimeout(() => { setQDebounced(q.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const fetchGRNs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { project_id: projectId || undefined, page, limit: pageSize, q: qDebounced || undefined };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const data = await apiGet('/stores/grn', params);
      setGRNs(data.items || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      console.error('Error fetching GRNs:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, pageSize, qDebounced, filters]);
  useEffect(() => { setPage(1); }, [projectId]);

  useEffect(() => { fetchGRNs(); }, [fetchGRNs]);

  const Chips = ({ items, k }) => (
    <div className="flex flex-wrap gap-1" data-testid={`grn-filter-${k}`}>
      {items.map(([v, l]) => (
        <button key={v} type="button" onClick={() => setFilter(k, v)} data-testid={`grn-filter-${k}-${v || 'all'}`}
          className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${filters[k] === v ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>{l}</button>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500" data-testid="grn-count">{total} GRNs</div>
        <Button data-testid="grn-create-btn" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-2" /> Create GRN
        </Button>
      </div>
      <div className="space-y-2" data-testid="grn-filters">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search GRN no, PO no, vendor, challan, vehicle…" className="pl-8" data-testid="grn-search" />
          </div>
          <Input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} className="w-36" data-testid="grn-date-from" title="From" />
          <Input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} className="w-36" data-testid="grn-date-to" title="To" />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          <Chips items={QC_CHIPS} k="qc_status" />
          <Chips items={TYPE_CHIPS} k="type" />
          <Chips items={LOC_CHIPS} k="location_type" />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">GRN Number</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">PO Number</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Project</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Location</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Vendor</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Evidence</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Inspection</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Value</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-8 text-gray-500">Loading...</td></tr>
                ) : grns.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-8 text-gray-500">No GRNs found</td></tr>
                ) : grns.map(grn => {
                  const doneCount = (grn.evidence_checks_done || Object.keys(grn.evidence || {})).length;
                  const cancelled = grn.status === 'cancelled';
                  return (
                    <tr key={grn.id} className={`hover:bg-gray-50 ${cancelled ? 'opacity-60' : ''}`} data-testid={`grn-row-${grn.id}`}>
                      <td className="px-4 py-3 font-mono font-medium"><span className="flex items-center gap-1.5">{grn.grn_no} <GRNStatusBadges grn={grn} /></span></td>
                      <td className="px-4 py-3 font-mono text-xs">{grn.is_non_po ? <span className="text-slate-400 font-sans">—</span> : grn.po_no}</td>
                      <td className="px-4 py-3">{grn.project_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-xs" data-testid={`grn-loc-${grn.id}`}>{LOC_SHORT[grn.receiving_location_type || 'project']}{grn.office_location_name ? ` · ${grn.office_location_name}` : ''}</td>
                      <td className="px-4 py-3">{grn.vendor_name}</td>
                      <td className="px-4 py-3 text-center">
                        {doneCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                            <ShieldCheck className="w-3.5 h-3.5" /> {doneCount}
                          </span>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {cancelled ? <span className="text-xs text-slate-500">—</span> : <InspectionBadge grn={grn} />}
                        {!cancelled && grn.inspection_status === 'pending' && (
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800" data-testid={`grn-qc-pending-${grn.id}`}>
                            QC Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">₹{grn.total_value?.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(grn.created_at)}</td>
                      <td className="px-4 py-3 text-center">
                        <Button variant="ghost" size="sm" data-testid={`grn-view-${grn.id}`} onClick={() => setViewGrn(grn)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t border-slate-200 px-3 py-2">
              <Pagination page={page} pageSize={pageSize} total={total}
                start={(page - 1) * pageSize} end={Math.min(page * pageSize, total)}
                onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
                label="GRNs" compact data-testid="grn-pagination" />
            </div>
          </div>
        </CardContent>
      </Card>

      {showCreate && (
        <CreateGRNModal
          projectId={projectId} projects={projects}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchGRNs(); }}
        />
      )}
      {viewGrn && (
        <GRNDetailModal
          grn={viewGrn} canInspect={canInspect} currentUser={user}
          onClose={() => setViewGrn(null)}
          onChanged={() => fetchGRNs()}
          onInspected={() => { fetchGRNs(); setViewGrn(null); }}
        />
      )}
    </div>
  );
}

// ── Evidence capture slot for the Create GRN modal ──
function EvidenceSlot({ checkKey, required, value, checked, remarks, onCapture, onClear, onToggleChecked, onRemarks }) {
  const meta = EVIDENCE_META[checkKey];
  const inputId = `grn-evidence-${checkKey}`;
  return (
    <div data-testid={`grn-evidence-slot-${checkKey}`}
      className={`rounded-lg border-2 p-3 transition-colors ${value ? 'border-emerald-400 bg-emerald-50' : required ? 'border-amber-300 bg-amber-50/40' : 'border-dashed border-gray-300'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-700">
          {meta.label} {required ? <span className="text-red-500">*</span> : <span className="text-gray-400">(optional)</span>}
        </span>
        {value && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
      </div>
      {value ? (
        <div className="relative">
          <img src={value} alt={meta.label} className="w-full h-28 object-cover rounded" />
          <div className="flex gap-2 mt-2">
            <label htmlFor={inputId} className="flex-1 cursor-pointer text-center text-xs py-1.5 rounded border border-gray-300 hover:bg-gray-50">Retake</label>
            <button type="button" data-testid={`grn-evidence-clear-${checkKey}`} onClick={() => onClear(checkKey)}
              className="px-2 py-1.5 text-xs rounded border border-gray-300 text-gray-500 hover:text-red-600">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <label htmlFor={inputId} data-testid={`grn-evidence-capture-${checkKey}`}
          className="flex flex-col items-center justify-center gap-1 h-28 cursor-pointer text-gray-400 hover:text-gray-600">
          <Camera className="w-7 h-7" />
          <span className="text-[10px] uppercase tracking-wide">Tap to capture</span>
          <span className="text-[10px] text-gray-400">{meta.hint}</span>
        </label>
      )}
      <label className="flex items-center gap-1.5 mt-2 cursor-pointer select-none" data-testid={`grn-evidence-checked-label-${checkKey}`}>
        <input type="checkbox" checked={!!checked} onChange={(e) => onToggleChecked(checkKey, e.target.checked)}
          className="w-3.5 h-3.5 accent-emerald-600" data-testid={`grn-evidence-checked-${checkKey}`} />
        <span className="text-[11px] text-gray-600">Verified OK</span>
      </label>
      <input type="text" value={remarks || ''} onChange={(e) => onRemarks(checkKey, e.target.value)}
        placeholder="Remarks (optional)" className="w-full mt-1.5 px-2 py-1 border border-gray-200 rounded text-[11px]"
        data-testid={`grn-evidence-remarks-${checkKey}`} />
      <input id={inputId} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onCapture(checkKey, f); e.target.value = ''; }} />
    </div>
  );
}

// ========== CREATE GRN MODAL ==========
export function CreateGRNModal({ projectId, projects = [], onClose, onCreated }) {
  const [selectedPO, setSelectedPO] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [challanNo, setChallanNo] = useState('');
  const [challanDate, setChallanDate] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [lrNo, setLrNo] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [qtyByBoq, setQtyByBoq] = useState({});
  const [binByBoq, setBinByBoq] = useState({});
  const [mfgByBoq, setMfgByBoq] = useState({});
  const [images, setImages] = useState({});
  const [checks, setChecks] = useState({});
  const [checkRemarks, setCheckRemarks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('po');
  const [npProject, setNpProject] = useState(projectId || '');
  const [npVendor, setNpVendor] = useState('');
  const [npVendorId, setNpVendorId] = useState('');
  const [npVendorPhone, setNpVendorPhone] = useState('');
  const [npVendorGstin, setNpVendorGstin] = useState('');
  const emptyNpLine = () => ({ description: '', unit: '', qty: '', rate: '', boq_id: '', boq: null, bin_location: '', material_id: '', manufacturing_date: '', item_mode: 'master' });
  const [npLines, setNpLines] = useState([emptyNpLine()]);
  const [threshold, setThreshold] = useState(5000);
  // Multi-vehicle delivery entries
  const [multiVehicle, setMultiVehicle] = useState(false);
  const [deliveryEntries, setDeliveryEntries] = useState([
    { vehicle_no: '', challan_no: '', challan_date: '', driver_name: '', images: {}, checks: {} }
  ]);
  // B-Admin §1 — receiving location
  const [receivingLocType, setReceivingLocType] = useState('project');
  const [officeLocationId, setOfficeLocationId] = useState('');
  const [officeLocationName, setOfficeLocationName] = useState('');
  const [officeLocations, setOfficeLocations] = useState([]);

  useEffect(() => {
    apiGet('/config-masters/office_locations')
      .then(d => setOfficeLocations(d?.values || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    apiGet('/settings/stores-config')
      .then(d => { if (d?.non_po_grn_threshold != null) setThreshold(Number(d.non_po_grn_threshold)); })
      .catch(() => {});
  }, []);

  const addNpLine = () => setNpLines(prev => [...prev, emptyNpLine()]);
  const removeNpLine = (i) => setNpLines(prev => prev.filter((_, idx) => idx !== i));
  const updateNpLine = (i, patch) => setNpLines(prev => prev.map((ln, idx) => (idx === i ? { ...ln, ...patch } : ln)));
  const pickNpBoq = (i, b) => updateNpLine(i, b
    ? { boq_id: b.id, boq: b, description: b.description || '', unit: b.unit || '', material_id: b.material_id || '', item_mode: 'boq' }
    : { boq_id: '', boq: null, item_mode: 'master', description: '', material_id: '' });

  const isProjectLoc = receivingLocType === 'project';
  const npTotal = npLines.reduce((s, ln) => s + Number(ln.qty || 0) * Number(ln.rate || 0), 0);
  const npOverLimit = npTotal > threshold + 0.001;
  const npValidLines = npLines.filter(ln => Number(ln.qty || 0) > 0 && ((ln.description || '').trim() || ln.boq_id || ln.material_id));
  // GRN fix #7 — Transporter / LR / Vehicle are optional for every Non-PO receipt (hand-carried petty purchases).
  const npCanSubmit = !submitting && (isProjectLoc ? !!npProject : (receivingLocType === 'office' ? !!officeLocationId : true))
    && !!npVendor.trim() && npValidLines.length > 0 && !npOverLimit && !!images.invoice_check;
  const npBlockReasons = [];
  if (isProjectLoc && !npProject) npBlockReasons.push('Select a project');
  if (receivingLocType === 'office' && !officeLocationId) npBlockReasons.push('Select an office location');
  if (!npVendor.trim()) npBlockReasons.push('Enter the shop / vendor name');
  if (npValidLines.length === 0) npBlockReasons.push('Add at least one line item (material or typed name) with a quantity');
  if (npOverLimit) npBlockReasons.push(`Total exceeds the ₹${threshold.toLocaleString('en-IN')} limit — raise a Purchase Order instead`);
  if (!images.invoice_check) npBlockReasons.push('Capture the bill / invoice photo');

  const handleSubmitNonPO = async () => {
    if (!npCanSubmit) return;
    setSubmitting(true);
    try {
      const items = npValidLines.map(ln => ({
        boq_id: ln.boq_id || null,
        description: (ln.description || '').trim() || null,
        unit: (ln.unit || '').trim() || null,
        received_qty: Number(ln.qty || 0),
        rate: Number(ln.rate || 0),
        bin_location: (ln.bin_location || '').trim() || null,
        material_id: ln.material_id || null,
        manufacturing_date: ln.manufacturing_date || null,
      }));
      const payload = {
        po_id: null, project_id: npProject || undefined,
        vendor_id: npVendorId || undefined, vendor_name: npVendor.trim(),
        vendor_phone: npVendorPhone.trim() || null, vendor_gstin: npVendorGstin.trim() || null,
        challan_no: challanNo || null, challan_date: challanDate || null, items,
        receiving_location_type: receivingLocType,
        office_location_id: receivingLocType === 'office' ? officeLocationId : undefined,
        office_location_name: receivingLocType === 'office' ? officeLocationName : undefined,
        transporter_name: transporterName.trim() || null,
        lr_no: lrNo.trim() || null,
        vehicle_no: vehicleNo.trim() || null,
        remarks: remarks || null,
        invoice_check_image: images.invoice_check || null,
        quantity_check_image: images.quantity_check || null,
        invoice_check_checked: !!checks.invoice_check,
        quantity_check_checked: !!checks.quantity_check,
        invoice_check_remarks: checkRemarks.invoice_check || null,
        quantity_check_remarks: checkRemarks.quantity_check || null,
      };
      const res = await fetch(`${API}/api/stores/grn`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) { toast.success('Non-PO GRN created'); onCreated(); }
      else { const err = await res.json().catch(() => ({})); toast.error(typeof err.detail === 'string' ? err.detail : 'Failed to create GRN'); }
    } catch { toast.error('Failed to create GRN'); } finally { setSubmitting(false); }
  };

  const selectPO = async (po) => {
    setSelectedPO(po);
    setRequirements(null);
    setQtyByBoq({});
    if (po) {
      try {
        const req = await apiGet(`/stores/po/${po.id}/grn-requirements`);
        setRequirements(req);
        // Seed Received Now with the PENDING quantity per line.
        const seed = {};
        (req.items || []).forEach((li) => { seed[li.boq_id] = li.pending_qty; });
        setQtyByBoq(seed);
      } catch (e) { console.error(e); toast.error('Could not load PO lines'); }
    }
  };

  const handleCapture = async (key, file) => {
    try {
      const dataUrl = await compressImageFile(file);
      setImages(prev => ({ ...prev, [key]: dataUrl }));
      setChecks(prev => (key in prev ? prev : { ...prev, [key]: true }));
    } catch { toast.error('Could not process photo, please retake'); }
  };
  const handleClear = (key) => setImages(prev => { const n = { ...prev }; delete n[key]; return n; });
  const handleToggleChecked = (key, val) => setChecks(prev => ({ ...prev, [key]: val }));
  const handleRemarks = (key, val) => setCheckRemarks(prev => ({ ...prev, [key]: val }));

  const requiredChecks = requirements?.required_checks || ['quantity_check', 'quality_check', 'invoice_check'];
  const allChecks = ['quantity_check', 'quality_check', 'invoice_check', 'mtc_check'];
  const missingRequired = requiredChecks.filter(c => !images[c]);
  // GRN fix #7 — logistics mandatory only for single-vehicle PROJECT SITE receipts (matches the server).
  const activeEntries = deliveryEntries.filter(e => e.vehicle_no.trim());
  const logisticsFilled = multiVehicle
    ? activeEntries.length > 0
    : (!isProjectLoc || (!!transporterName.trim() && !!lrNo.trim() && !!vehicleNo.trim()));
  // GRN fix #8 — every vehicle needs Quantity + Quality photos (+ MTC when required).
  const entryChecks = ['quantity_check', 'quality_check', ...(requirements?.mtc_required ? ['mtc_check'] : [])];
  const entriesMissingPhotos = multiVehicle && activeEntries.some(e => entryChecks.some(c => !e.images?.[c]));
  const poLines = requirements?.items || [];
  const overReceipt = poLines.some((li) => Number(qtyByBoq[li.boq_id] || 0) > Number(li.pending_qty || 0) + 0.0001);
  const canSubmit = selectedPO && !!requirements && !submitting && logisticsFilled && !overReceipt && (
    multiVehicle ? (!!images.invoice_check && !entriesMissingPhotos) : missingRequired.length === 0
  );
  const poBlockReasons = [];
  if (!selectedPO) poBlockReasons.push('Select a Purchase Order');
  if (selectedPO && !logisticsFilled) poBlockReasons.push(multiVehicle ? 'Enter at least one vehicle number' : 'Fill Transporter Name, LR No. and Vehicle No. (project site receipt)');
  if (overReceipt) poBlockReasons.push('Received quantity exceeds the pending quantity on one or more lines');
  if (selectedPO && (multiVehicle ? !images.invoice_check : missingRequired.length > 0)) {
    poBlockReasons.push(multiVehicle ? 'Capture the shared invoice photo' : 'Capture all required evidence photos');
  }
  if (entriesMissingPhotos) poBlockReasons.push(`Capture ${requirements?.mtc_required ? 'Quantity, Quality and MTC' : 'Quantity and Quality'} photos for every vehicle`);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const items = poLines.map(li => ({
        boq_id: li.boq_id,
        received_qty: Number(qtyByBoq[li.boq_id] || 0),
        bin_location: (binByBoq[li.boq_id] || '').trim() || null,
        manufacturing_date: mfgByBoq[li.boq_id] || null,
      })).filter(i => i.received_qty > 0);
      if (items.length === 0) { toast.error('Enter received quantity for at least one item'); setSubmitting(false); return; }

      const deliveryEntriesPayload = multiVehicle
        ? activeEntries.map(e => ({
              vehicle_no: e.vehicle_no.trim(),
              challan_no: e.challan_no || null,
              challan_date: e.challan_date || null,
              driver_name: e.driver_name || null,
              quantity_check_image: e.images?.quantity_check || null,
              quality_check_image: e.images?.quality_check || null,
              mtc_check_image: e.images?.mtc_check || null,
              quantity_check_checked: !!e.checks?.quantity_check,
              quality_check_checked: !!e.checks?.quality_check,
              mtc_check_checked: !!e.checks?.mtc_check,
            }))
        : null;

      const payload = {
        po_id: selectedPO.id,
        receiving_location_type: receivingLocType,
        office_location_id: receivingLocType === 'office' ? officeLocationId : undefined,
        office_location_name: receivingLocType === 'office' ? officeLocationName : undefined,
        challan_no: multiVehicle ? null : (challanNo || null),
        challan_date: multiVehicle ? null : (challanDate || null),
        transporter_name: multiVehicle ? null : (transporterName.trim() || null),
        lr_no: multiVehicle ? null : (lrNo.trim() || null),
        vehicle_no: multiVehicle ? null : (vehicleNo.trim() || null),
        items, remarks: remarks || null,
        quantity_check_image: multiVehicle ? null : (images.quantity_check || null),
        quality_check_image: multiVehicle ? null : (images.quality_check || null),
        invoice_check_image: images.invoice_check || null,  // always shared
        mtc_check_image: multiVehicle ? null : (images.mtc_check || null),
        quantity_check_checked: multiVehicle ? null : (!!checks.quantity_check),
        quality_check_checked: multiVehicle ? null : (!!checks.quality_check),
        invoice_check_checked: !!checks.invoice_check,
        mtc_check_checked: multiVehicle ? null : (!!checks.mtc_check),
        quantity_check_remarks: checkRemarks.quantity_check || null,
        quality_check_remarks: checkRemarks.quality_check || null,
        invoice_check_remarks: checkRemarks.invoice_check || null,
        mtc_check_remarks: checkRemarks.mtc_check || null,
        delivery_entries: deliveryEntriesPayload,
      };
      const res = await fetch(`${API}/api/stores/grn`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (res.ok) { toast.success('GRN created with evidence captured'); onCreated(); }
      else { const err = await res.json().catch(() => ({})); toast.error(typeof err.detail === 'string' ? err.detail : 'Failed to create GRN'); }
    } catch { toast.error('Failed to create GRN'); } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg w-full ${mode === 'non_po' ? 'max-w-5xl' : 'max-w-3xl'} max-h-[92vh] overflow-y-auto`} data-testid="grn-create-modal">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Create GRN — Goods Receipt</h2>
            <Button variant="ghost" onClick={onClose}><XCircle className="w-5 h-5" /></Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Captured by Stores · photographic evidence is mandatory</p>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex gap-2" data-testid="grn-mode-toggle">
            <button type="button" data-testid="grn-mode-po" onClick={() => setMode('po')}
              className={`px-3 py-1.5 text-xs rounded-full border ${mode === 'po' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-gray-600 border-gray-300'}`}>Against PO</button>
            <button type="button" data-testid="grn-mode-nonpo" onClick={() => setMode('non_po')}
              className={`px-3 py-1.5 text-xs rounded-full border ${mode === 'non_po' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-gray-600 border-gray-300'}`}>Non-PO (petty)</button>
          </div>

          {/* B-Admin §1 — Receiving location */}
          <div className="p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid="grn-receiving-location">
            <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider">Receiving Location</label>
            <div className="flex gap-2 mb-2">
              {[['project', 'Project Site'], ['office', 'Office'], ['godown', 'Central Godown']].map(([v, l]) => (
                <button key={v} type="button" data-testid={`grn-loc-${v}`}
                  onClick={() => { setReceivingLocType(v); setOfficeLocationId(''); setOfficeLocationName(''); }}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${receivingLocType === v ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>{l}</button>
              ))}
            </div>
            {receivingLocType === 'office' && (
              <select data-testid="grn-office-location-select" value={officeLocationId}
                onChange={e => { const o = officeLocations.find(x => x.id === e.target.value); setOfficeLocationId(e.target.value); setOfficeLocationName(o?.name || ''); }}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
                <option value="">Select office location *</option>
                {officeLocations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
            {receivingLocType !== 'project' && (
              <p className="text-xs text-slate-500 mt-1">Transporter / LR / Vehicle are optional for office &amp; godown receipts.</p>
            )}
          </div>

          {mode === 'po' && (<>
            <div>
              <label className="block text-sm font-medium mb-1">Purchase Order *</label>
              <POSearchSelect value={selectedPO} onSelect={selectPO} projectId={projectId || undefined} testid="grn-po-select" />
            </div>
            {selectedPO && (<>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Challan No.</label>
                  <Input data-testid="grn-challan-no" value={challanNo} onChange={(e) => setChallanNo(e.target.value)} placeholder="Delivery challan #" disabled={multiVehicle} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Challan Date</label>
                  <Input data-testid="grn-challan-date" type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)} disabled={multiVehicle} />
                </div>
              </div>

              {/* Logistics mandatory only for single-vehicle project-site receipts (GRN fix #7) */}
              {!multiVehicle && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="grn-logistics">
                  <div>
                    <label className="block text-sm font-medium mb-1">Transporter Name {isProjectLoc ? '*' : ''}</label>
                    <Input data-testid="grn-transporter-name" value={transporterName} onChange={(e) => setTransporterName(e.target.value)} placeholder={isProjectLoc ? 'e.g. VRL Logistics' : 'Optional'} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">LR No. {isProjectLoc ? '*' : ''}</label>
                    <Input data-testid="grn-lr-no" value={lrNo} onChange={(e) => setLrNo(e.target.value)} placeholder={isProjectLoc ? 'Lorry Receipt #' : 'Optional'} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Vehicle No. {isProjectLoc ? '*' : ''}</label>
                    <Input data-testid="grn-vehicle-no" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder={isProjectLoc ? 'e.g. MH12AB1234' : 'Optional'} />
                  </div>
                </div>
              )}

              {/* Multi-vehicle toggle */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <input type="checkbox" id="multi-vehicle-toggle" checked={multiVehicle}
                  onChange={e => { setMultiVehicle(e.target.checked); }}
                  className="w-4 h-4 accent-[#3b4872]"
                  data-testid="grn-multi-vehicle-toggle"
                />
                <label htmlFor="multi-vehicle-toggle" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Multiple vehicles / trips for this delivery
                </label>
                <span className="text-xs text-slate-400 ml-auto">Each vehicle gets its own photos. Invoice is shared.</span>
              </div>

              {/* Per-vehicle delivery entries */}
              {multiVehicle && (
                <div className="space-y-3" data-testid="grn-delivery-entries">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Vehicle / Trip Entries</label>
                    <button type="button"
                      onClick={() => setDeliveryEntries(prev => [...prev, { vehicle_no: '', challan_no: '', challan_date: '', driver_name: '', images: {}, checks: {} }])}
                      className="text-xs text-[#3b4872] font-semibold flex items-center gap-1 hover:underline"
                      data-testid="grn-add-delivery-entry"
                    >
                      <Plus className="w-3 h-3" /> Add Vehicle
                    </button>
                  </div>
                  {deliveryEntries.map((entry, ei) => (
                    <div key={ei} className="border border-slate-200 rounded-lg p-3 bg-white space-y-3" data-testid={`grn-delivery-entry-${ei}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Vehicle {ei + 1}</span>
                        {deliveryEntries.length > 1 && (
                          <button type="button" onClick={() => setDeliveryEntries(prev => prev.filter((_, i) => i !== ei))}
                            className="text-rose-400 hover:text-rose-600 text-xs" data-testid={`grn-remove-entry-${ei}`}>
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <label className="text-xs text-slate-500 font-medium">Vehicle No. *</label>
                          <Input placeholder="e.g. MH12AB1234" value={entry.vehicle_no}
                            onChange={e => setDeliveryEntries(prev => prev.map((en, i) => i === ei ? { ...en, vehicle_no: e.target.value } : en))}
                            data-testid={`grn-entry-vehicle-${ei}`} />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 font-medium">Challan No.</label>
                          <Input placeholder="Challan #" value={entry.challan_no || ''}
                            onChange={e => setDeliveryEntries(prev => prev.map((en, i) => i === ei ? { ...en, challan_no: e.target.value } : en))}
                            data-testid={`grn-entry-challan-${ei}`} />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 font-medium">Challan Date</label>
                          <Input type="date" value={entry.challan_date || ''}
                            onChange={e => setDeliveryEntries(prev => prev.map((en, i) => i === ei ? { ...en, challan_date: e.target.value } : en))}
                            data-testid={`grn-entry-date-${ei}`} />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 font-medium">Driver Name</label>
                          <Input placeholder="Driver name" value={entry.driver_name || ''}
                            onChange={e => setDeliveryEntries(prev => prev.map((en, i) => i === ei ? { ...en, driver_name: e.target.value } : en))}
                            data-testid={`grn-entry-driver-${ei}`} />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {['quantity_check', 'quality_check', 'mtc_check'].map(ck => (
                          <EvidenceSlot key={ck} checkKey={ck} required={ck !== 'mtc_check' || !!requirements?.mtc_required}
                            value={entry.images?.[ck]}
                            checked={entry.checks?.[ck]}
                            remarks={null}
                            onCapture={(key, file) => {
                              compressImageFile(file).then(dataUrl => {
                                setDeliveryEntries(prev => prev.map((en, i) => i === ei
                                  ? { ...en, images: { ...en.images, [key]: dataUrl }, checks: { ...en.checks, [key]: true } }
                                  : en
                                ));
                              });
                            }}
                            onClear={(key) => setDeliveryEntries(prev => prev.map((en, i) => i === ei
                              ? { ...en, images: { ...en.images, [key]: undefined } } : en))}
                            onToggleChecked={(key, val) => setDeliveryEntries(prev => prev.map((en, i) => i === ei
                              ? { ...en, checks: { ...en.checks, [key]: val } } : en))}
                            onRemarks={() => {}}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">Received Quantities</label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr>
                      <th className="text-left px-3 py-2">Item</th>
                      <th className="text-center px-3 py-2">Unit</th>
                      <th className="text-right px-3 py-2" title="Pending (Ordered − already received)">Pending</th>
                      <th className="text-right px-3 py-2">Received Now</th>
                      <th className="text-left px-3 py-2">Bin / Location</th>
                      <th className="text-left px-3 py-2">Mfg. Date</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {!requirements && <tr><td colSpan={6} className="px-3 py-4 text-center text-xs text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading PO lines…</td></tr>}
                      {poLines.map(bi => {
                        const pending = Number(bi.pending_qty || 0);
                        const ordered = Number(bi.quantity || 0);
                        const already = ordered - pending;
                        const rejected = Number(bi.rejected_qty || 0);
                        const now = Number(qtyByBoq[bi.boq_id] || 0);
                        const over = now > pending + 0.0001;
                        return (
                          <tr key={bi.boq_id} className={over ? 'bg-rose-50' : ''}>
                            <td className="px-3 py-2">{(bi.description || bi.item_code || '').substring(0, 40)}</td>
                            <td className="px-3 py-2 text-center">{bi.unit}</td>
                            <td className="px-3 py-2 text-right" title={`Ordered ${ordered}, already counted ${already.toFixed(2)}`}>
                              <span className="font-semibold tabular-nums" data-testid={`grn-pending-${bi.boq_id}`}>{pending.toFixed(2)}</span>
                              {already > 0 && (
                                <div className="text-[10px] text-slate-400">
                                  of {ordered} · {already.toFixed(2)} rcvd
                                  {Number(bi.awaiting_qc_qty || 0) > 0 && (
                                    <span className="text-amber-600"> ({Number(bi.awaiting_qc_qty).toFixed(2)} awaiting QC)</span>
                                  )}
                                </div>
                              )}
                              {rejected > 0 && <div className="text-[10px] text-rose-600" data-testid={`grn-rejected-${bi.boq_id}`}>{rejected.toFixed(2)} rejected, replacement due</div>}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input data-testid={`grn-qty-${bi.boq_id}`} type="number" min="0" max={pending}
                                value={qtyByBoq[bi.boq_id] ?? ''}
                                onChange={(e) => setQtyByBoq(prev => ({ ...prev, [bi.boq_id]: e.target.value }))}
                                className={`w-24 border rounded px-2 py-1 text-right ${over ? 'border-rose-400 bg-rose-50' : ''}`} />
                              {over && <div className="text-[10px] text-rose-600 mt-0.5">Max {pending.toFixed(2)}</div>}
                            </td>
                            <td className="px-3 py-2">
                              <input data-testid={`grn-bin-${bi.boq_id}`} value={binByBoq[bi.boq_id] || ''}
                                onChange={(e) => setBinByBoq(prev => ({ ...prev, [bi.boq_id]: e.target.value }))}
                                placeholder="e.g. Rack B3" className="w-32 border rounded px-2 py-1 text-sm" />
                            </td>
                            <td className="px-3 py-2">
                              <input data-testid={`grn-mfg-${bi.boq_id}`} type="date" value={mfgByBoq[bi.boq_id] || ''}
                                onChange={(e) => setMfgByBoq(prev => ({ ...prev, [bi.boq_id]: e.target.value }))}
                                className="w-32 border rounded px-2 py-1 text-sm" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    {multiVehicle ? 'Shared Invoice Photo (required)' : 'Receipt Evidence (camera only)'}
                  </label>
                  {requirements && <span className="text-xs text-gray-500">{requirements.mtc_required ? 'MTC required for this material' : 'MTC not required'}</span>}
                </div>
                {multiVehicle ? (
                  <div className="grid grid-cols-2 gap-3 max-w-sm">
                    <EvidenceSlot checkKey="invoice_check" required={true}
                      value={images.invoice_check} checked={checks.invoice_check} remarks={checkRemarks.invoice_check}
                      onCapture={handleCapture} onClear={handleClear} onToggleChecked={handleToggleChecked} onRemarks={handleRemarks} />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {allChecks.map(key => (
                      <EvidenceSlot key={key} checkKey={key} required={requiredChecks.includes(key)}
                        value={images[key]} checked={checks[key]} remarks={checkRemarks[key]}
                        onCapture={handleCapture} onClear={handleClear} onToggleChecked={handleToggleChecked} onRemarks={handleRemarks} />
                    ))}
                  </div>
                )}
                {missingRequired.length > 0 && !multiVehicle && <p className="text-xs text-amber-600 mt-2" data-testid="grn-evidence-warning">Capture all required photos to enable submission.</p>}
                {multiVehicle && !images.invoice_check && <p className="text-xs text-amber-600 mt-2">Invoice photo is required.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Remarks</label>
                <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="w-full border rounded-lg px-3 py-2 h-16" placeholder="Optional remarks..." />
              </div>
            </>)}
          </>)}

          {mode === 'non_po' && (<>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              For small purchases without a PO (e.g. nails, consumables). Capped at <b>₹{threshold.toLocaleString('en-IN')}</b> per GRN — anything higher must go through a Purchase Order.
            </div>
            <div className="grid grid-cols-2 gap-4">
              {isProjectLoc && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Project *</label>
                  <ProjectSearchSelect projects={projects} value={npProject} onSelect={(id) => setNpProject(id)} testid="grn-np-project" />
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Shop / vendor name *</label>
                <VendorNameInput name={npVendor} vendorId={npVendorId}
                  onChange={({ name, vendorId, vendor }) => { setNpVendor(name); setNpVendorId(vendorId || ''); if (vendor?.gstin) setNpVendorGstin(vendor.gstin); }}
                  testid="grn-np-vendor" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Shop phone</label>
                <Input data-testid="grn-np-vendor-phone" value={npVendorPhone} onChange={(e) => setNpVendorPhone(e.target.value)} placeholder="Optional" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">GSTIN (if on bill)</label>
                <Input data-testid="grn-np-vendor-gstin" value={npVendorGstin} onChange={(e) => setNpVendorGstin(e.target.value.toUpperCase())} placeholder="Optional" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Bill No.</label>
                <Input data-testid="grn-np-challan-no" value={challanNo} onChange={(e) => setChallanNo(e.target.value)} placeholder="Cash bill #" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Bill Date</label>
                <Input data-testid="grn-np-challan-date" type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)} />
              </div>
            </div>
            {/* GRN fix #7 — logistics optional for every Non-PO receipt */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="grn-np-logistics">
              <div>
                <label className="block text-sm font-medium mb-1">Transporter Name</label>
                <Input data-testid="grn-np-transporter-name" value={transporterName} onChange={(e) => setTransporterName(e.target.value)} placeholder="Optional (hand-carried?)" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">LR No.</label>
                <Input data-testid="grn-np-lr-no" value={lrNo} onChange={(e) => setLrNo(e.target.value)} placeholder="Optional" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vehicle No.</label>
                <Input data-testid="grn-np-vehicle-no" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Items</label>
                <Button size="sm" variant="outline" data-testid="grn-np-add-line" onClick={addNpLine}><Plus className="w-3.5 h-3.5 mr-1" /> Add item</Button>
              </div>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-gray-50"><tr>
                    <th className="text-left px-2 py-2 min-w-[320px]">Item (Material Master pick or typed name)</th>
                    <th className="text-center px-2 py-2">Unit</th>
                    <th className="text-right px-2 py-2">Qty</th>
                    <th className="text-right px-2 py-2">Rate ₹</th>
                    <th className="text-right px-2 py-2">Amount</th>
                    <th className="text-left px-2 py-2">Bin / Location</th>
                    <th className="text-left px-2 py-2">Mfg. Date</th>
                    <th></th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {npLines.map((ln, i) => (
                      <tr key={i} data-testid={`grn-np-line-${i}`} className="align-top">
                        <td className="px-2 py-1.5 space-y-1">
                          {npProject && (
                            <BOQSearchSelect projectId={npProject} value={ln.boq} onSelect={(b) => pickNpBoq(i, b)} testid={`grn-np-boq-${i}`} />
                          )}
                          {ln.item_mode !== 'boq' && (
                            <NonPOItemInput line={ln} onChange={(patch) => updateNpLine(i, patch)} testid={`grn-np-item-${i}`} />
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center"><input value={ln.unit} onChange={(e) => updateNpLine(i, { unit: e.target.value })} className="w-14 border rounded px-1 py-1 text-xs text-center" placeholder="Nos" /></td>
                        <td className="px-2 py-1.5 text-right"><input type="number" min="0" value={ln.qty} onChange={(e) => updateNpLine(i, { qty: e.target.value })} data-testid={`grn-np-qty-${i}`} className="w-20 border rounded px-2 py-1 text-xs text-right" /></td>
                        <td className="px-2 py-1.5 text-right"><input type="number" min="0" value={ln.rate} onChange={(e) => updateNpLine(i, { rate: e.target.value })} data-testid={`grn-np-rate-${i}`} className="w-24 border rounded px-2 py-1 text-xs text-right" /></td>
                        <td className="px-2 py-1.5 text-right text-xs font-medium">₹{(Number(ln.qty || 0) * Number(ln.rate || 0)).toLocaleString('en-IN')}</td>
                        <td className="px-2 py-1.5"><input value={ln.bin_location || ''} onChange={(e) => updateNpLine(i, { bin_location: e.target.value })} placeholder="Rack/Bin" data-testid={`grn-np-bin-${i}`} className="w-28 border rounded px-2 py-1 text-xs" /></td>
                        <td className="px-2 py-1.5"><input type="date" value={ln.manufacturing_date || ''} onChange={(e) => updateNpLine(i, { manufacturing_date: e.target.value })} data-testid={`grn-np-mfg-${i}`} className="w-32 border rounded px-2 py-1 text-xs" /></td>
                        <td className="px-1"><button onClick={() => removeNpLine(i)} data-testid={`grn-np-remove-${i}`} className="text-rose-500 hover:text-rose-700"><XCircle className="w-4 h-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center mt-2 text-sm">
                <span className={`font-medium ${npOverLimit ? 'text-rose-600' : 'text-gray-700'}`} data-testid="grn-np-total">Total: ₹{npTotal.toLocaleString('en-IN')}</span>
                <span className="text-xs text-gray-500">Limit: ₹{threshold.toLocaleString('en-IN')}</span>
              </div>
              {npOverLimit && <p className="text-xs text-rose-600 mt-1" data-testid="grn-np-over-limit">Total exceeds the ₹{threshold.toLocaleString('en-IN')} limit — raise a Purchase Order instead.</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Bill / Invoice Photo * <span className="text-xs font-normal text-gray-500">(+ optional quantity photo)</span></label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <EvidenceSlot checkKey="invoice_check" required value={images.invoice_check} checked={checks.invoice_check} remarks={checkRemarks.invoice_check} onCapture={handleCapture} onClear={handleClear} onToggleChecked={handleToggleChecked} onRemarks={handleRemarks} />
                <EvidenceSlot checkKey="quantity_check" required={false} value={images.quantity_check} checked={checks.quantity_check} remarks={checkRemarks.quantity_check} onCapture={handleCapture} onClear={handleClear} onToggleChecked={handleToggleChecked} onRemarks={handleRemarks} />
              </div>
              {!images.invoice_check && <p className="text-xs text-amber-600 mt-2" data-testid="grn-np-bill-warning">A bill / invoice photo is required.</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Remarks</label>
              <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="w-full border rounded-lg px-3 py-2 h-16" placeholder="Optional remarks..." />
            </div>
          </>)}
        </div>
        <div className="p-6 border-t bg-gray-50 sticky bottom-0">
          {mode === 'po' && poBlockReasons.length > 0 && (
            <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="grn-po-missing-summary">
              <div className="font-medium mb-1">Before you can submit:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {poBlockReasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {mode === 'non_po' && npBlockReasons.length > 0 && (
            <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="grn-np-missing-summary">
              <div className="font-medium mb-1">Before you can submit:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {npBlockReasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {mode === 'po' ? (
            <Button data-testid="grn-submit-btn" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Create GRN
            </Button>
          ) : (
            <Button data-testid="grn-submit-btn" onClick={handleSubmitNonPO} disabled={!npCanSubmit}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Create Non-PO GRN
            </Button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

